// GhostCamera — v0.328.0 (2026-11-04)
// ---------------------------------------------------------------------------
// THE GHOST CAMERA: the close-out shot for an item. When the office or a
// subbie marks an item fixed, the live camera opens with the item's ORIGINAL
// photo laid faintly over it, so whoever fixed it stands where the defect was
// photographed and lines the shot up. Then a Before | After check: Retake, or
// Use photo. The caller saves the file as the AFTER of that photo (Kind
// 'after', BeforePhotoId) and the pair shows on the card, the client's page
// and the PDF (v0.317).
//
// Ported from InSpec's field app (the same camera, the same rules, so the
// two apps take the same shot):
//   - Ghost slider: how strong the original shows (remembered per device).
//   - Hold Original: full strength while the finger is down.
//   - The frame takes the original's shape and both pictures fill it the way
//     object-fit: cover does; the shot is cut to exactly what was on screen
//     (coverCrop), so the pair lines up side by side.
//   - No live camera (no getUserMedia, permission refused, no lens): the
//     device's own camera through a file input, then the same check.
//
// Colours: a viewfinder is dark in BOTH themes (like PhotoLightbox), so the
// camera ground and the on-picture controls are local constants, not theme
// tokens; the slider accent and Use photo are the brand.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Eye, Camera, RotateCcw, Check } from 'lucide-react';
import { theme, btnPrimary } from '../theme';
import { fitBox, coverCrop, ghostSrc } from '../utils/ghostMath';

const OPACITY_KEY = 'droppin_ghost_opacity_v1';
const DEFAULT_OPACITY = 40;   // percent
const MAX_EDGE = 2400;        // px - the caller compresses further

// Dark in both themes on purpose (see the header).
const GROUND = '#0b1220';
const ON_PICTURE = '#ffffff';
const PILL = 999;
const CONTROL = 10;

function readOpacity() {
  try {
    const raw = localStorage.getItem(OPACITY_KEY);
    const n = Number(raw);
    return raw != null && n >= 0 && n <= 90 ? n : DEFAULT_OPACITY;
  } catch (e) { return DEFAULT_OPACITY; }
}
function saveOpacity(n) {
  try { localStorage.setItem(OPACITY_KEY, String(n)); } catch (e) { /* convenience only */ }
}

// Start the live feed. play() rejects with AbortError when a newer srcObject
// interrupts it - harmless, the newer play() carries on. Any other refusal
// leaves a black frame, so hand over to the device's own camera instead.
function playFeed(video, onFail) {
  const p = video.play();
  if (p && p.catch) {
    p.catch((e) => {
      if (e && e.name === 'AbortError') return;
      console.warn('[ghost-camera] live feed would not play', e);
      onFail();
    });
  }
}

// Glass controls over the picture - the photo viewer's language.
const glass = {
  background: 'rgba(255,255,255,0.18)',
  border: '1px solid rgba(255,255,255,0.35)',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  color: ON_PICTURE, fontFamily: 'inherit', cursor: 'pointer',
};
const caps = { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' };
const primary = {
  ...btnPrimary, minHeight: 44, padding: '0 18px', fontSize: 15,
  display: 'inline-flex', alignItems: 'center', gap: 6,
};

// maxEdge: the subbie's box sends the photo as a data URL under a 2 MB cap, so
// it asks for a smaller shot (still above the 1600 px copy the PDF uses).
export default function GhostCamera({ beforePhoto, onUse, onClose, maxEdge = MAX_EDGE }) {
  const beforeSrc = ghostSrc(beforePhoto);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const stageRef = useRef(null);
  const fileRef = useRef(null);

  const [mode, setMode] = useState('starting');   // 'starting' | 'live' | 'fallback' | 'review'
  const [aspect, setAspect] = useState(3 / 4);     // the original's w/h
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const [opacity, setOpacity] = useState(readOpacity);
  const [peek, setPeek] = useState(false);
  const [shot, setShot] = useState(null);          // { file, url }
  const [busy, setBusy] = useState(false);

  // Start the lens. Any failure: the device's own camera.
  useEffect(() => {
    let cancelled = false;
    const md = typeof navigator !== 'undefined' && navigator.mediaDevices;
    if (!md || typeof md.getUserMedia !== 'function') { setMode('fallback'); return undefined; }
    md.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1920 } }, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) { v.srcObject = stream; playFeed(v, () => { if (!cancelled) setMode('fallback'); }); }
        setMode('live');
      })
      .catch(() => { if (!cancelled) setMode('fallback'); });
    return () => {
      cancelled = true;
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  // Retake remounts the <video> (the review swaps it out) - give it the stream again.
  useEffect(() => {
    const v = videoRef.current;
    const s = streamRef.current;
    if (mode !== 'live' || !v || !s || v.srcObject === s) return;
    v.srcObject = s;
    playFeed(v, () => setMode('fallback'));
  }, [mode]);

  // The frame fits the space between the top bar and the controls.
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => setStage({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Esc closes the camera only - never the card under it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      if (onClose) onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Free the review picture's object URL when it is replaced or we close.
  useEffect(() => () => { if (shot && shot.url) URL.revokeObjectURL(shot.url); }, [shot]);

  const onBeforeLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (w > 0 && h > 0) setAspect(w / h);
  };

  const takeShot = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || busy) return;
    setBusy(true);
    const crop = coverCrop(v.videoWidth, v.videoHeight, aspect);
    const scale = Math.min(1, maxEdge / Math.max(crop.sw, crop.sh));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(crop.sw * scale);
    canvas.height = Math.round(crop.sh * scale);
    canvas.getContext('2d').drawImage(v, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      setBusy(false);
      if (!blob) return;
      const file = new File([blob], `after-${Date.now()}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
      setShot({ file, url: URL.createObjectURL(blob) });
      setMode('review');
    }, 'image/jpeg', 0.9);
  }, [aspect, busy, maxEdge]);

  const onFallbackFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setShot({ file: f, url: URL.createObjectURL(f) });
    setMode('review');
  };

  const retake = () => {
    setShot(null);
    if (streamRef.current) setMode('live');
    else { setMode('fallback'); if (fileRef.current) fileRef.current.click(); }
  };

  const changeOpacity = (n) => { setOpacity(n); saveOpacity(n); };

  const frame = fitBox(aspect, stage.w, stage.h);
  const ghostAlpha = peek ? 1 : opacity / 100;
  const live = mode === 'live' || mode === 'starting';

  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label="After photo" data-ghost-mode={mode}
      // A portal still bubbles through the React tree - keep taps here from
      // reaching the card that opened it.
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: GROUND, color: ON_PICTURE,
        display: 'flex', flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
        userSelect: 'none', WebkitUserSelect: 'none',
      }}
    >
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', flexShrink: 0 }}>
        <button
          type="button" onClick={onClose} aria-label="Close the camera"
          style={{ ...glass, width: 40, height: 40, borderRadius: PILL, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}
        ><X size={20} /></button>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{mode === 'review' ? 'Before and after' : 'Line it up with the original'}</div>
          <div style={{ fontSize: 12, opacity: 0.75 }}>
            {mode === 'review' ? 'Check they match, then use the photo.'
              : mode === 'fallback' ? 'Stand where the defect photo was taken.'
              : 'Stand where it was taken. Hold Original to compare.'}
          </div>
        </div>
      </div>

      {/* Picture. margin, not padding: clientWidth counts padding, and the
          frame is sized from it. */}
      <div ref={stageRef} style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 12px' }}>
        {mode === 'review' && shot ? (
          <ReviewPair beforeSrc={beforeSrc} afterSrc={shot.url} aspect={aspect} stage={stage} />
        ) : (
          <div data-ghost-frame style={{ position: 'relative', flexShrink: 0, width: frame.w, height: frame.h, borderRadius: CONTROL, overflow: 'hidden', background: GROUND }}>
            <video
              ref={videoRef} playsInline muted autoPlay
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: live ? 'block' : 'none' }}
            />
            {beforeSrc && (
              <img
                src={beforeSrc} alt="" onLoad={onBeforeLoad} draggable={false} data-ghost-before
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: mode === 'fallback' ? 1 : ghostAlpha, pointerEvents: 'none' }}
              />
            )}
            {mode === 'fallback' && (
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 14, background: 'rgba(2,6,12,0.72)', fontSize: 14, lineHeight: 1.45 }}>
                This device did not open the live camera here. Take the photo with the normal camera from the same spot as this one.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{ flexShrink: 0, padding: '12px 16px 16px', width: '100%', maxWidth: 560, alignSelf: 'center', boxSizing: 'border-box' }}>
        {live && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <span style={{ ...caps, width: 48 }}>Ghost</span>
              <input
                type="range" min={0} max={90} step={5} value={opacity}
                onChange={(e) => changeOpacity(Number(e.target.value))}
                aria-label="Ghost opacity"
                style={{ flex: 1, accentColor: theme.brand, height: 28 }}
              />
              <span style={{ fontSize: 12, fontWeight: 700, width: 38, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{opacity}%</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
              <button
                type="button"
                onPointerDown={(e) => { e.preventDefault(); setPeek(true); }}
                onPointerUp={() => setPeek(false)}
                onPointerLeave={() => setPeek(false)}
                onPointerCancel={() => setPeek(false)}
                onContextMenu={(e) => e.preventDefault()}
                disabled={!beforeSrc}
                aria-label="Hold to see original"
                style={{ ...glass, justifySelf: 'start', minHeight: 44, padding: '0 14px', borderRadius: PILL, fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', touchAction: 'none' }}
              ><Eye size={16} /> Original</button>
              <button
                type="button" onClick={takeShot} disabled={mode !== 'live' || busy}
                aria-label="Take the photo"
                style={{
                  width: 74, height: 74, borderRadius: PILL, padding: 0, cursor: 'pointer',
                  background: 'transparent', border: `4px solid ${ON_PICTURE}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  opacity: mode === 'live' && !busy ? 1 : 0.5,
                }}
              ><span style={{ width: 58, height: 58, borderRadius: PILL, background: ON_PICTURE }} /></button>
              <span />
            </div>
          </>
        )}
        {mode === 'fallback' && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onFallbackFile} />
            <button type="button" onClick={() => fileRef.current && fileRef.current.click()} style={primary}>
              <Camera size={18} /> Open the camera
            </button>
          </div>
        )}
        {mode === 'review' && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
            <button type="button" onClick={retake} style={{ ...glass, minHeight: 44, padding: '0 18px', borderRadius: CONTROL, fontSize: 15, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <RotateCcw size={16} /> Retake
            </button>
            <button type="button" onClick={() => shot && onUse && onUse(shot.file)} style={primary}>
              <Check size={18} /> Use photo
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Before | After, side by side when the pictures are tall, stacked when wide.
function ReviewPair({ beforeSrc, afterSrc, aspect, stage }) {
  const sideBySide = aspect <= 1;
  const gap = 8;
  const box = sideBySide
    ? fitBox(aspect, Math.max(0, (stage.w - gap) / 2), stage.h - 24)
    : fitBox(aspect, stage.w, Math.max(0, (stage.h - gap) / 2 - 24));
  const one = (label, src) => (
    <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ width: box.w, height: box.h, borderRadius: CONTROL, overflow: 'hidden', background: GROUND }}>
        {src && <img src={src} alt={label} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      </div>
      <figcaption style={caps}>{label}</figcaption>
    </figure>
  );
  return (
    <div data-ghost-review style={{ display: 'flex', flexDirection: sideBySide ? 'row' : 'column', gap, alignItems: 'center', justifyContent: 'center' }}>
      {one('Before', beforeSrc)}
      {one('After', afterSrc)}
    </div>
  );
}
